import { OfferApiService } from '@/_api/offer.api.service';
import {
	Accommodation,
	CountRoomType,
	Description,
	DescriptionTemplate,
	MatProduct,
	MatPromotion,
	OptionalTrip,
	PriceSettingsResult,
	SellTerm,
	SellTransport,
	SellTransportStatistics,
	ProductStop,
} from '@/_store/current-offer/current-offer.types';
import { Result } from '@/common/data.service';
import { FetchOfferStatistics } from '@/offer/fetch-offer-statistics/fetch-offer-statistics.service';
import { OfferDropdownItemMode } from '@/offer/new-offer-configurator/offer-dropdown-item/offer-dropdown-item.component';
import { LocationDescriptions, OfferService } from '@/offer/offer.model';
import { isPlatformServer } from '@angular/common';
import { EnvironmentInjector, Inject, Injectable, PLATFORM_ID, inject, runInInjectionContext } from '@angular/core';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { combineLatest, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import * as currentOfferActions from './current-offer.actions';
import { CurrentOfferFacade } from './current-offer.facade';
import { filterPromotionsByDateRange, findBestTerm, findPromo, pickSellTermAfterFetch } from './current-offer.utils';
import { Store } from '@ngrx/store';
import {
	selectActiveFilterValues,
	selectActiveProduct,
	selectActiveSellTerm,
	selectFirstUrlParamsApplied,
	selectIsOfferDataReady,
} from './current-offer.selectors';

@Injectable()
export class CurrentOfferEffects {
	constructor(
		private readonly actions$: Actions,
		private readonly offerApiService: OfferApiService,
		private readonly activatedRoute: ActivatedRoute,
		private readonly router: Router,
		@Inject(PLATFORM_ID) private readonly platformId: string,
		private readonly environmentInjector: EnvironmentInjector,
		private readonly currentOfferFacade: CurrentOfferFacade,
		private readonly store: Store,
	) {}

	fetchMatProducts$ = createEffect(() =>
		this.actions$.pipe(
			ofType(currentOfferActions.fetchMatProduct),
			switchMap(({ id }) =>
				this.offerApiService.getMatProduct(id).pipe(
					map((product: MatProduct) => currentOfferActions.fetchMatProductSuccess({ product })),
					catchError((error) => of(currentOfferActions.fetchMatProductError({ error }))),
				),
			),
		),
	);

	fetchMatProductError$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(currentOfferActions.fetchMatProductError),
				tap(() => {
					if (isPlatformServer(this.platformId)) {
						return;
					}

					this.router.navigate(['/404'], {
						skipLocationChange: true,
					});
				}),
			),
		{ dispatch: false },
	);

	fetchOptionalTrip$ = createEffect(() =>
		this.actions$.pipe(
			ofType(currentOfferActions.fetchOptionalTrip),
			switchMap(({ locationIds }) =>
				this.offerApiService.getOptionalTrip(locationIds).pipe(
					map((result: Result<OptionalTrip>) =>
						currentOfferActions.fetchOptionalTripSuccess({
							optionalTrip: result.results,
						}),
					),
					catchError((error) =>
						of(
							currentOfferActions.fetchOptionalTripError({
								error,
							}),
						),
					),
				),
			),
		),
	);

	fetchLocations$ = createEffect(() =>
		this.actions$.pipe(
			ofType(currentOfferActions.fetchLocations),
			switchMap(({ ids }) =>
				this.offerApiService.getLocations(ids).pipe(
					map((locations: LocationDescriptions[]) =>
						currentOfferActions.fetchLocationsSuccess({
							locations,
						}),
					),
					catchError((error) =>
						of(
							currentOfferActions.fetchLocationsError({
								error,
							}),
						),
					),
				),
			),
		),
	);

	fetchDescription$ = createEffect(() =>
		this.actions$.pipe(
			ofType(currentOfferActions.fetchDescription),
			switchMap(() =>
				this.offerApiService.getDescription().pipe(
					map((result: Result<Description>) =>
						currentOfferActions.fetchDescriptionSuccess({
							descriptions: result.results,
						}),
					),
					catchError((error) =>
						of(
							currentOfferActions.fetchDescriptionError({
								error,
							}),
						),
					),
				),
			),
		),
	);

	fetchDescriptionTemplate$ = createEffect(() =>
		this.actions$.pipe(
			ofType(currentOfferActions.fetchDescriptionTemplate),
			switchMap(() =>
				this.offerApiService.getDescriptionTemplate().pipe(
					map((result: Result<DescriptionTemplate>) =>
						currentOfferActions.fetchDescriptionTemplateSuccess({
							descriptionTemplates: result.results,
						}),
					),
					catchError((error) =>
						of(
							currentOfferActions.fetchDescriptionTemplateError({
								error,
							}),
						),
					),
				),
			),
		),
	);

	fetchSellTransport$ = createEffect(() =>
		this.actions$.pipe(
			ofType(currentOfferActions.fetchSellTransport),
			switchMap(({ id }) =>
				this.offerApiService.getOfferSellTransport(id).pipe(
					map((result: Array<SellTransport>) =>
						currentOfferActions.fetchSellTransportSuccess({
							sellTransport: result,
						}),
					),
					catchError((error) =>
						of(
							currentOfferActions.fetchSellTransportError({
								error,
							}),
						),
					),
				),
			),
		),
	);

	fetchAccommodation$ = createEffect(() =>
		this.actions$.pipe(
			ofType(currentOfferActions.fetchAccommodation),
			switchMap(({ id }) =>
				this.offerApiService.getAccommodation(id).pipe(
					map((result: Array<Accommodation>) =>
						currentOfferActions.fetchAccommodationSuccess({
							accommodation: result,
						}),
					),
					catchError((error) =>
						of(
							currentOfferActions.fetchAccommodationError({
								error,
							}),
						),
					),
				),
			),
		),
	);

	fetchSellTerm$ = createEffect(() =>
		this.actions$.pipe(
			ofType(currentOfferActions.fetchSellTerm),
			switchMap(({ params }) =>
				this.offerApiService.getOfferSellTerm(params).pipe(
					map((result: Result<SellTerm>) =>
						currentOfferActions.fetchSellTermSuccess({
							sellTerms: result.results,
						}),
					),
					catchError((error) =>
						of(
							currentOfferActions.fetchSellTermError({
								error,
							}),
						),
					),
				),
			),
		),
	);

	fetchSellTermSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(currentOfferActions.fetchSellTermSuccess),
			withLatestFrom(this.activatedRoute.queryParams, this.store.select(selectActiveFilterValues)),
			switchMap(([{ sellTerms }, queryParams, activeFilterValues]) => {
				const availableFromStop = sellTerms.filter((t) => t.transportAvailableFromStop === true);
				const pool = availableFromStop.length > 0 ? availableFromStop : sellTerms;

				const nightsAmountFilter = activeFilterValues?.nightsAmountFilter;
				const picked = pickSellTermAfterFetch(pool, queryParams, nightsAmountFilter);
				const term = picked?.term ?? findBestTerm(sellTerms);
				const syncNightsChip = picked?.syncNightsChip ?? false;

				const dateRange = {
					upper: term.dateRange.upper,
					lower: term.dateRange.lower,
					termPrice: term.termPrice,
					termId: term.id,
					transportFrom: term.transportFrom,
					transportTo: term.transportTo,
					nights: term.nights,
					omnibusPrice: term.omnibusPrice ?? null,
					maxRooms: term.maxRooms,
					transportKind: term.transportKind,
				};

				const updateTerm = currentOfferActions.updateFilterTerm({ dateRange });

				if (syncNightsChip) {
					const tk = activeFilterValues?.transportKindFilter;
					const transportKind = tk?.length ? tk : [term.transportKind];
					return of(
						currentOfferActions.changeTermsFilters({
							nights: term.nights,
							transportKind,
						}),
						updateTerm,
					);
				}

				return of(updateTerm);
			}),
		),
	);

	fetchPriceSettings$ = createEffect(() =>
		this.actions$.pipe(
			ofType(currentOfferActions.fetchPriceSettings),
			switchMap(({ id }) =>
				this.offerApiService.getOfferPriceSettings(id).pipe(
					map((result: PriceSettingsResult) =>
						currentOfferActions.fetchPriceSettingsSuccess({
							priceSettings: result.calculatedPriceSetting,
						}),
					),
					catchError((error) =>
						of(
							currentOfferActions.fetchPriceSettingsError({
								error,
							}),
						),
					),
				),
			),
		),
	);

	fetchOfferService$ = createEffect(() =>
		this.actions$.pipe(
			ofType(currentOfferActions.fetchOfferService),
			switchMap(({ params }) =>
				this.offerApiService.getOfferService(params).pipe(
					map((result: Result<OfferService>) =>
						currentOfferActions.fetchOfferServiceSuccess({
							offerService: result.results,
						}),
					),
					catchError((error) =>
						of(
							currentOfferActions.fetchOfferServiceError({
								error,
							}),
						),
					),
				),
			),
		),
	);

	fetchPromotions$ = createEffect(() =>
		this.actions$.pipe(
			ofType(currentOfferActions.fetchPromotions),
			switchMap(({ params: params, dateRange: dateRange }) =>
				this.offerApiService.getPromotions(params).pipe(
					map((result: Result<MatPromotion>) => {
						return currentOfferActions.fetchPromotionsSuccess({
							promotions: filterPromotionsByDateRange(result.results, dateRange),
							allPromotions: result.results,
						});
					}),
					catchError((error) =>
						of(
							currentOfferActions.fetchPromotionsError({
								error,
							}),
						),
					),
				),
			),
		),
	);

	fetchPromotionsSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(currentOfferActions.fetchPromotionsSuccess),
			withLatestFrom(this.activatedRoute.queryParams),
			map(([value, queryParams]) =>
				currentOfferActions.updateFilterPromotion({
					promotion: value?.promotions ? findPromo(value.promotions, queryParams) : null,
				}),
			),
		),
	);

	fetchCountRoomType$ = createEffect(() =>
		this.actions$.pipe(
			ofType(currentOfferActions.fetchCountRoomTypes),
			switchMap(({ termId, productId }) =>
				this.offerApiService.getCountRoomTypes(productId, termId).pipe(
					map((roomTypesCount: CountRoomType) =>
						currentOfferActions.fetchCountRoomTypesSuccess({ roomTypesCount }),
					),
					catchError((error) =>
						of(
							currentOfferActions.fetchCountRoomTypesError({
								error,
							}),
						),
					),
				),
			),
		),
	);

	fetchSellTransportStatistics$ = createEffect(() =>
		this.actions$.pipe(
			ofType(currentOfferActions.fetchSellTransportStatistics),
			switchMap(({ termId }) =>
				this.offerApiService.getSellTransportStatistics(termId).pipe(
					map((sellTransportStatistics: SellTransportStatistics) =>
						currentOfferActions.fetchSellTransportStatisticsSuccess({ sellTransportStatistics }),
					),
					catchError((error) =>
						of(
							currentOfferActions.fetchSellTransportStatisticsError({
								error,
							}),
						),
					),
				),
			),
		),
	);

	fetchProductStops$ = createEffect(() =>
		this.actions$.pipe(
			ofType(currentOfferActions.fetchProductStops),
			withLatestFrom(this.store.select(selectActiveSellTerm)),
			filter(([, activeSellTerm]) => !!activeSellTerm),
			switchMap(([{ productId }, activeSellTerm]) =>
				this.offerApiService.getProductStops(productId, activeSellTerm.id).pipe(
					map((productStops: ProductStop[]) =>
						currentOfferActions.fetchProductStopsSuccess({
							productStops,
						}),
					),
					catchError((error) =>
						of(
							currentOfferActions.fetchProductStopsError({
								error,
							}),
						),
					),
				),
			),
		),
	);

	clearFilterValue$ = createEffect(() =>
		this.actions$.pipe(
			ofType(currentOfferActions.clearFilterValue),
			map(({ option }) => {
				switch (option) {
					case OfferDropdownItemMode.MAT_TERM:
						return currentOfferActions.updateFilterTerm(null);
					case OfferDropdownItemMode.BOARDS:
						return currentOfferActions.updateFilterBoard(null);
					case OfferDropdownItemMode.DEPARTURE:
						return currentOfferActions.updateFilterStop(null);
					case OfferDropdownItemMode.PARTICIPANTS:
						return currentOfferActions.updateFilterParticipants(null);
					case OfferDropdownItemMode.ROOM_TYPES:
						return currentOfferActions.updateFilterRoomType(null);
					case OfferDropdownItemMode.PROMOTIONS:
						return currentOfferActions.updateFilterPromotion(null);
					case OfferDropdownItemMode.EMAIL:
					case OfferDropdownItemMode.PHONE:
					case OfferDropdownItemMode.FIRSTNAME:
					case OfferDropdownItemMode.LASTNAME:
						return;
					default:
						const value: never = option;
						throw new Error(`There is no option like ${value}`);
				}
			}),
		),
	);

	fetchDataOnUpdateFilterTerm$ = createEffect(() =>
		this.actions$.pipe(
			ofType(currentOfferActions.updateFilterTerm),
			filter(({ dateRange }) => !!dateRange),
			withLatestFrom(this.store.select(selectActiveProduct)),
			tap(([{ dateRange }]) => {
				runInInjectionContext(this.environmentInjector, () => {
					inject(FetchOfferStatistics).getNewStatistics(dateRange.termId);
				});
			}),
			switchMap(([{ dateRange }, product]) =>
				of(
					currentOfferActions.fetchPriceSettings({
						id: dateRange.termId,
					}),
					currentOfferActions.fetchSellTransport({
						id: dateRange.termId,
					}),
					currentOfferActions.fetchAccommodation({
						id: dateRange.termId,
					}),
					currentOfferActions.fetchProductStops({
						productId: product!.id,
					}),
				),
			),
		),
	);

	offerDataReady$ = createEffect(() =>
		combineLatest([
			this.actions$.pipe(ofType(currentOfferActions.fetchPriceSettingsSuccess)),
			this.actions$.pipe(ofType(currentOfferActions.fetchPromotionsSuccess)),
			this.actions$.pipe(ofType(currentOfferActions.fetchSellTransportSuccess)),
		]).pipe(map(() => currentOfferActions.offerDataReady())),
	);

	firstUrlParamsUpdateAfterReady$ = createEffect(() =>
		this.store.select(selectIsOfferDataReady).pipe(
			distinctUntilChanged(),
			filter((isReady) => isReady),
			filter(() => this.router.url.startsWith('/oferta')),
			switchMap(() =>
				this.currentOfferFacade.urlParams$.pipe(
					filter((urlParams) => urlParams.term_id !== 'undefined' && urlParams.stops !== undefined),
					take(1),
				),
			),
			tap((urlParams) => {
				const navigationExtras: NavigationExtras = {
					queryParams: {
						...urlParams,
					},
					replaceUrl: true,
				};
				this.router.navigate([], navigationExtras);
			}),
			map(() => currentOfferActions.firstUrlParamsApplied()),
		),
	);

	updateUrlParams$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(
					currentOfferActions.setMultiRoomMode,
					currentOfferActions.updateFilterBoard,
					currentOfferActions.updateFilterStop,
					currentOfferActions.updateFilterRoomType,
					currentOfferActions.updateFilterParticipants,
					currentOfferActions.updateFilterTerm,
					currentOfferActions.updateFilterPromotion,
				),
				withLatestFrom(
					this.store.select(selectIsOfferDataReady),
					this.store.select(selectFirstUrlParamsApplied),
					this.currentOfferFacade.urlParams$,
				),
				filter(([, isReady]) => isReady),
				filter(([, , firstApplied]) => firstApplied),
				filter(([, , , urlParams]) => urlParams.term_id !== 'undefined'),
				filter(() => this.router.url.startsWith('/oferta')),
				tap(([, , , urlParams]) => {
					const navigationExtras: NavigationExtras = {
						queryParams: {
							...urlParams,
						},
						replaceUrl: true,
					};
					this.router.navigate([], navigationExtras);
				}),
			),
		{ dispatch: false },
	);
}
